import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { Commitment, FixedCommitment } from "../@data";
import {
  getAllCommitments,
  getCommitmentsByDate,
  getCommitmentsByProfessionalAndDate,
  createCommitment as createCommitmentService,
  updateCommitment as updateCommitmentService,
  deleteCommitment as deleteCommitmentService,
  getAllFixedCommitments,
  getFixedCommitmentsByProfessional,
} from "../services/appointments";
import { useNotification } from "./notification";

export interface CommitmentsContextModel {
  commitments: Commitment[];
  fixedCommitments: FixedCommitment[];
  loading: boolean;
  currentDate: string | null;
  fetchCommitments: () => void;
  fetchCommitmentsByDate: (date: string) => void;
  fetchCommitmentsByProfessionalAndDate: (
    professionalId: string,
    date: string
  ) => Promise<void>;
  createCommitment: (
    data: Omit<Commitment, "id" | "createdAt" | "updatedAt">
  ) => Promise<void>;
  updateCommitment: (
    id: string,
    data: Partial<Omit<Commitment, "id" | "createdAt" | "updatedAt">>
  ) => Promise<void>;
  deleteCommitment: (id: string) => Promise<void>;
  fetchFixedCommitments: () => void;
  fetchFixedCommitmentsByProfessional: (
    professionalId: string
  ) => Promise<void>;
}

const CommitmentsContext = createContext<CommitmentsContextModel>(
  {} as CommitmentsContextModel
);

export const CommitmentsProvider = ({ children }: { children: ReactNode }) => {
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [fixedCommitments, setFixedCommitments] = useState<FixedCommitment[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState<string | null>(null);
  const { handleNotification } = useNotification();

  const fetchCommitments = useCallback(() => {
    setLoading(true);
    getAllCommitments()
      .then((data) => setCommitments(data))
      .catch((error: any) => {
        handleNotification("error", "Erro ao buscar compromissos");
      })
      .finally(() => setLoading(false));
  }, []);

  const fetchCommitmentsByDate = useCallback((date: string) => {
    setLoading(true);
    setCurrentDate(date);
    getCommitmentsByDate(date)
      .then((data) => setCommitments(data))
      .catch((error: any) => {
        handleNotification("error", "Erro ao buscar compromissos por data");
      })
      .finally(() => setLoading(false));
  }, []);

  const fetchCommitmentsByProfessionalAndDate = useCallback(
    async (professionalId: string, date: string) => {
      setLoading(true);
      setCurrentDate(date);
      try {
        const data = await getCommitmentsByProfessionalAndDate(
          professionalId,
          date
        );
        setCommitments(data);
      } catch (error: any) {
        handleNotification(
          "error",
          "Erro ao buscar compromissos do profissional"
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const createCommitment = useCallback(
    async (data: Omit<Commitment, "id" | "createdAt" | "updatedAt">) => {
      setLoading(true);
      try {
        await createCommitmentService(data);
        // Recarrega apenas os compromissos da data atual
        if (currentDate) {
          fetchCommitmentsByDate(currentDate);
        }
        handleNotification("success", "Compromisso criado com sucesso!");
      } catch (error: any) {
        handleNotification("error", "Erro ao criar compromisso");
      } finally {
        setLoading(false);
      }
    },
    [currentDate, fetchCommitmentsByDate]
  );

  const updateCommitment = useCallback(
    async (
      id: string,
      data: Partial<Omit<Commitment, "id" | "createdAt" | "updatedAt">>
    ) => {
      setLoading(true);
      try {
        await updateCommitmentService(id, data);
        // Recarrega apenas os compromissos da data atual
        if (currentDate) {
          fetchCommitmentsByDate(currentDate);
        }
        handleNotification("success", "Compromisso atualizado com sucesso!");
      } catch (error: any) {
        handleNotification("error", "Erro ao atualizar compromisso");
      } finally {
        setLoading(false);
      }
    },
    [currentDate, fetchCommitmentsByDate]
  );

  const deleteCommitment = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        await deleteCommitmentService(id);
        // Recarrega apenas os compromissos da data atual
        if (currentDate) {
          fetchCommitmentsByDate(currentDate);
        }
        handleNotification("success", "Compromisso excluído com sucesso!");
      } catch (error: any) {
        handleNotification("error", "Erro ao excluir compromisso");
      } finally {
        setLoading(false);
      }
    },
    [currentDate, fetchCommitmentsByDate]
  );

  // Funções para FixedCommitments
  const fetchFixedCommitments = useCallback(() => {
    setLoading(true);
    getAllFixedCommitments()
      .then((data) => setFixedCommitments(data))
      .catch((error: any) => {
        handleNotification("error", "Erro ao buscar compromissos fixos");
      })
      .finally(() => setLoading(false));
  }, []);

  const fetchFixedCommitmentsByProfessional = useCallback(
    async (professionalId: string) => {
      setLoading(true);
      try {
        const data = await getFixedCommitmentsByProfessional(professionalId);
        setFixedCommitments(data);
      } catch (error: any) {
        handleNotification(
          "error",
          "Erro ao buscar compromissos fixos do profissional"
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return (
    <CommitmentsContext.Provider
      value={{
        commitments,
        fixedCommitments,
        loading,
        currentDate,
        fetchCommitments,
        fetchCommitmentsByDate,
        fetchCommitmentsByProfessionalAndDate,
        createCommitment,
        updateCommitment,
        deleteCommitment,
        fetchFixedCommitments,
        fetchFixedCommitmentsByProfessional,
      }}
    >
      {children}
    </CommitmentsContext.Provider>
  );
};

export const useCommitments = () => {
  const context = useContext(CommitmentsContext);
  if (!context) {
    throw new Error(
      "useCommitments deve ser usado dentro de um CommitmentsProvider"
    );
  }
  return context;
};
